import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, Typography, CircularProgress, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import timerIcon from "../../../assets/Images/CheckoutReminder/timer.svg";
import close16 from "../../../assets/Images/CheckoutReminder/close-16.svg";
import close14 from "../../../assets/Images/CheckoutReminder/close-14.svg";
import heartBadge from "../../../assets/Images/CheckoutReminder/heart-badge.svg";
import heartRed from "../../../assets/Images/CheckoutReminder/heart-red.svg";
import heartWhite from "../../../assets/Images/CheckoutReminder/heart-white.svg";
import bellDark from "../../../assets/Images/CheckoutReminder/bell-dark.svg";
import bellRed from "../../../assets/Images/CheckoutReminder/bell-red.svg";
import bellWhite from "../../../assets/Images/CheckoutReminder/bell-white.svg";
import calendarIcon from "../../../assets/Images/CheckoutReminder/calendar.svg";
import checkWhite from "../../../assets/Images/CheckoutReminder/check-white.svg";
import lockIcon from "../../../assets/Images/CheckoutReminder/lock.svg";
import { Images } from "../../../utils/Images";
import { createReminder } from "../../../api/services/reminderService";
import { validateIndianMobile } from "../../../utils/phoneValidation";

// "Finish your donation" popup shown when a donor presses Back after closing the Razorpay
// checkout without paying (Figma: Website Revamp 3199:6109 → 3204:6257 → 3204:7057).
// Shared by the temple pages (temple campaign page and /temples). `source` tags the saved
// reminder ("temple_checkout_close"); the NGO page has its own copy on feature/ngo-donationbox-generic.
// Step 1 recover (complete / smaller amount / remind me / leave), step 2 reminder form,
// step 3 reminder saved. Everything shown comes from the campaign and the abandoned checkout.

const RED = "#F04951";
const INK = "#1D1D1B";
const FONT = "Red Hat Display";
const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("en-IN");

// Icon frame with the SVG laid out the way the design does (group inset inside the frame).
const Icon = ({ src, size, inset, bleed }) => (
  <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0, overflow: "hidden" }}>
    {inset ? (
      <Box sx={{ position: "absolute", inset }}>
        <Box sx={{ position: "absolute", inset: bleed }}>
          <Box component="img" src={src} alt="" sx={{ display: "block", width: "100%", height: "100%", maxWidth: "none" }} />
        </Box>
      </Box>
    ) : (
      <Box component="img" src={src} alt="" sx={{ position: "absolute", inset: 0, display: "block", width: "100%", height: "100%", maxWidth: "none" }} />
    )}
  </Box>
);

const Rupee = ({ value }) => (
  <>
    <Box component="span" sx={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}>₹</Box>
    {fmt(value)}
  </>
);

const primaryBtnSx = {
  width: "100%",
  border: "none",
  borderRadius: "12px",
  background: RED,
  boxShadow: "0px 10px 24px 0px rgba(240,73,81,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  px: "20px",
  cursor: "pointer",
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "20px",
  color: "#FFFFFF",
  transition: "opacity 0.15s, transform 0.1s",
  "&:hover": { opacity: 0.92 },
  "&:active": { transform: "translateY(1px)" },
  "&:disabled": { opacity: 0.7, cursor: "default" },
  "&:focus-visible": { outline: `2px solid ${INK}`, outlineOffset: "2px" },
};

const outlineBtnSx = {
  width: "100%",
  borderRadius: "12px",
  border: "1.5px solid rgba(29,29,27,0.22)",
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  px: "20px",
  py: "14px",
  cursor: "pointer",
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "20px",
  color: INK,
  "&:hover": { background: "#FAFAFA" },
  "&:focus-visible": { outline: `2px solid ${INK}`, outlineOffset: "2px" },
};

const textLinkSx = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  py: "4px",
  fontFamily: FONT,
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: "18px",
  width: "100%",
  "&:hover": { textDecoration: "underline" },
  "&:focus-visible": { outline: `2px solid ${INK}`, outlineOffset: "2px", borderRadius: "4px" },
};

const CloseButton = ({ onClick, onImage, compact, sx }) => (
  <Box
    component="button"
    type="button"
    aria-label="Close"
    onClick={onClick}
    sx={{
      position: "absolute",
      top: onImage && compact ? "11px" : "16px",
      right: onImage && compact ? "11.6px" : "16px",
      width: onImage ? (compact ? "29.8px" : "36px") : "34px",
      height: onImage ? (compact ? "29.8px" : "36px") : "34px",
      borderRadius: "50%",
      border: "none",
      background: onImage ? "rgba(255,255,255,0.95)" : "#F3F3F3",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      zIndex: 2,
      p: 0,
      "&:focus-visible": { outline: `2px solid ${INK}`, outlineOffset: "2px" },
      ...sx,
    }}
  >
    <Icon src={onImage ? close16 : close14} size={onImage ? (compact ? "13.25px" : "16px") : "14px"} />
  </Box>
);

// Saved seva card. `size`: undefined = desktop, "remind" / "done" = the two phone frames
// (Figma Mobile revamp 872:1476 / 872:1477).
const SAVED_SIZES = {
  desktop: { thumb: "52px", pad: { p: "12px" }, gap: "12px", title: ["13px", "18px"], amount: ["16px", "20px"] },
  remind: { thumb: "38px", pad: { pl: "10px", pr: "12px", py: "8px" }, gap: "10px", title: ["12px", "18px"], amount: ["14px", "20px"] },
  done: { thumb: "42px", pad: { p: "12px" }, gap: "12px", title: ["12px", "18px"], amount: ["15px", "20px"] },
};
const SavedDonation = ({ image, title, amount, size }) => {
  const z = SAVED_SIZES[size] || SAVED_SIZES.desktop;
  return (
    <Box sx={{ width: "100%", background: "#F7F7F7", borderRadius: "14px", ...z.pad, display: "flex", gap: z.gap, alignItems: "center", boxSizing: "border-box" }}>
      <Box component="img" src={image || Images.fallBackImageCard} alt="" sx={{ width: z.thumb, height: z.thumb, borderRadius: "10px", objectFit: "cover", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "10px", lineHeight: "13px", letterSpacing: "1.5px", color: RED }}>
          SAVED SEVA
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: z.title[0],
            lineHeight: z.title[1],
            color: INK,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: z.amount[0], lineHeight: z.amount[1], color: INK, whiteSpace: "nowrap", flexShrink: 0 }}>
        <Rupee value={amount} />
      </Typography>
    </Box>
  );
};

const fieldLabelSx = (m) => ({ fontFamily: FONT, fontWeight: 500, fontSize: m ? "12px" : "13px", lineHeight: "18px", color: INK });
const inputSx = (hasError, m) => ({
  width: "100%",
  height: m ? "36px" : "46px",
  boxSizing: "border-box",
  border: `1px solid ${hasError ? RED : "#E3E3E3"}`,
  borderRadius: "10px",
  background: "#FFFFFF",
  px: m ? "12px" : "14px",
  fontFamily: FONT,
  fontWeight: 400,
  fontSize: m ? "12px" : "14px",
  lineHeight: "20px",
  color: INK,
  outline: "none",
  "&::placeholder": { color: "#9A9A9A", opacity: 1 },
  "&:focus": { borderColor: hasError ? RED : "#B9B9B8" },
});

// Smaller amounts offered on step 1: 10% / 20% / 50% of what they were giving, rounded to ₹100.
const smallerAmounts = (base) => {
  const out = [];
  [0.1, 0.2, 0.5].forEach((f) => {
    const v = Math.max(100, Math.round((base * f) / 100) * 100);
    if (v < base && !out.includes(v)) out.push(v);
  });
  return out;
};

const CheckoutReminderModal = ({ open, campaign, abandoned, onClose, onLeave, onCompleteDonation, source = "temple_checkout_close" }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [step, setStep] = useState("recover"); // recover | remind | done
  const [chosenBase, setChosenBase] = useState(null); // a smaller amount picked on step 1
  const [completing, setCompleting] = useState(false);

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const dateAnchorRef = useRef(null);

  // Each time it opens: back to step 1, reminder form prefilled from the checkout details
  useEffect(() => {
    if (!open) return;
    setStep("recover");
    setChosenBase(null);
    setErrors({});
    setFormError("");
    setDate(null);
    setName(abandoned?.form?.name || "");
    setEmail(abandoned?.form?.email || "");
    setMobile(String(abandoned?.form?.phone || "").replace(/\D/g, "").slice(-10));
  }, [open, abandoned]);

  const title = campaign?.title || "";
  const image = campaign?.bannerImage || campaign?.image || "";
  const raised = Number(campaign?.raisedAmount) || 0;
  const goal = Number(campaign?.goalAmount) || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const showProgress = Number(campaign?.hideAll) !== 1 && Number(campaign?.hide_goal) !== 1 && goal > 0;
  const isUrgent = Number(campaign?.FeatureUrgentCampaign) === 1;

  // What was being given. One-time money donations include the support fee; a smaller amount
  // keeps the same fee percentage. Product carts can't be scaled down, so no smaller amounts.
  const total = Number(abandoned?.amount) || 0;
  const tip = Number(abandoned?.tipAmount) || 0;
  const base = Math.max(0, total - tip);
  const tipRate = base > 0 ? tip / base : 0;
  const chips = useMemo(() => (abandoned?.kind === "products" ? [] : smallerAmounts(base)), [abandoned, base]);
  const chargeFor = (b) => b + Math.round(b * tipRate);
  const payAmount = chosenBase ? chargeFor(chosenBase) : total;
  const frequencyLabel =
    abandoned?.frequency === "monthly" ? "/month" : abandoned?.frequency === "quarterly" ? "/quarter" : "";

  const complete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      await onCompleteDonation?.({
        amount: payAmount,
        tipAmount: chosenBase ? Math.round(chosenBase * tipRate) : tip,
      });
    } finally {
      setCompleting(false);
    }
  };

  const saveReminder = async () => {
    setFormError("");
    const errs = {};
    if (!name.trim()) errs.name = true;
    const hasMobile = mobile.trim() !== "";
    const hasEmail = email.trim() !== "";
    let mobileDigits = "";
    if (hasMobile) {
      const check = validateIndianMobile(mobile);
      if (!check.isValid) errs.mobile = check.error;
      else mobileDigits = check.digits;
    }
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = "Enter a valid email address";
    if (!hasMobile && !hasEmail) errs.contact = true;
    if (!date) errs.date = true;
    setErrors(errs);
    if (Object.keys(errs).length) {
      setFormError(
        errs.contact
          ? "Enter at least one — mobile number or email."
          : errs.mobile || errs.email || (errs.name ? "Enter your name." : "Choose the date you'd like to donate.")
      );
      return;
    }

    setSaving(true);
    try {
      const reminderDate = date.format("YYYY-MM-DD");
      await createReminder({
        name: name.trim(),
        ...(hasEmail && { email: email.trim() }),
        ...(mobileDigits && { mobile: mobileDigits }),
        reminder_date: reminderDate,
        campaign_id: campaign?.id,
        campaign_name: title,
        source,
      });
      setStep("done");
    } catch (err) {
      const msg = err?.response?.data?.errors?.[0]?.msg || err?.errors?.[0]?.msg || err?.message;
      setFormError(msg ? `Couldn't save the reminder: ${msg}` : "Couldn't save the reminder. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const paperWidth = step === "recover" ? 520 : 480;
  const m = isMobile;
  // Phones: Figma Mobile revamp 872:1475 (recover, 300 wide) / 872:1476 / 872:1477 (326 wide)
  const mobileWidth = step === "recover" ? 300 : 326;
  const closeSx = m ? { top: "10px", right: "10px" } : {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      aria-labelledby="ngo-reminder-title"
      PaperProps={{
        sx: {
          width: m ? `min(${mobileWidth}px, calc(100vw - 32px))` : `min(${paperWidth}px, calc(100vw - 32px))`,
          maxWidth: "none",
          m: m ? "16px" : "32px",
          borderRadius: m ? (step === "recover" ? "22px" : "24px") : "24px",
          overflow: "hidden",
          boxShadow: "0px 30px 80px 0px rgba(0,0,0,0.35)",
          position: "relative",
        },
      }}
      slotProps={{ backdrop: { sx: { background: "rgba(29,29,27,0.72)" } } }}
    >
      {step === "recover" && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {/* Campaign image with close and heart badge (desktop also shows the urgency tag) */}
          <Box sx={{ position: "relative", width: "100%", height: m ? "81px" : "170px", flexShrink: 0 }}>
            <Box component="img" src={image || Images.fallBackImageCard} alt="" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0) 50%, rgba(26,5,5,0.5))" }} />
            {isUrgent && !m && (
              <Box sx={{ position: "absolute", top: "16px", left: "16px", background: "rgba(255,255,255,0.95)", borderRadius: "100px", px: "12px", py: "6px", display: "flex", gap: "6px", alignItems: "center" }}>
                <Icon src={timerIcon} size="14px" inset="8.33% 16.67% 12.5% 16.67%" bleed="-5.79% -6.87% -5.79% -6.88%" />
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "12px", lineHeight: "16px", color: INK, whiteSpace: "nowrap" }}>
                  Urgent
                </Typography>
              </Box>
            )}
            <CloseButton onImage onClick={onClose} compact={m} />
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: m ? "-15px" : "-34px",
                width: m ? "52px" : "68px",
                height: m ? "52px" : "68px",
                boxSizing: "border-box",
                borderRadius: m ? "26px" : "34px",
                border: m ? "4px solid #FFFFFF" : "5px solid #FFFFFF",
                background: RED,
                boxShadow: m ? "0px 7.6px 18px 0px rgba(240,73,81,0.45)" : "0px 10px 24px 0px rgba(240,73,81,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <Icon src={heartBadge} size={m ? "22.7px" : "30px"} />
            </Box>
          </Box>

          <Box sx={{ width: "100%", boxSizing: "border-box", pt: m ? "34px" : "52px", pb: m ? "18px" : "26px", px: m ? "16px" : "34px", display: "flex", flexDirection: "column", gap: m ? "8px" : "16px", alignItems: "center" }}>
            <Typography id="ngo-reminder-title" component="h2" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: m ? "17.5px" : "26px", lineHeight: m ? "normal" : "34px", color: INK, textAlign: "center" }}>
              You're Just One Step Away
              <Box component="span" sx={{ display: "block", color: RED }}>
                From Offering Your Seva
              </Box>
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: m ? "12px" : "15px", lineHeight: m ? "22px" : "24px", color: "#6B6560", textAlign: "center" }}>
              Your details are saved. Just one step left.
            </Typography>

            {/* Campaign progress */}
            <Box sx={{ width: "100%", boxSizing: "border-box", background: "#F7F7F7", borderRadius: m ? "13px" : "14px", px: m ? "12px" : "18px", py: m ? "12px" : "16px", display: "flex", flexDirection: "column", gap: m ? "8px" : "10px" }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: m ? "12px" : "14px", lineHeight: m ? "18.4px" : "20px", color: INK }}>{title}</Typography>
              {showProgress && (
                <>
                  <Box sx={{ width: "100%", height: m ? "7.4px" : "8px", borderRadius: "4px", background: "#E6E6E6", overflow: "hidden" }}>
                    <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: "4px", background: RED }} />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: m ? "nowrap" : "wrap" }}>
                    <Box sx={{ display: "flex", gap: m ? "5.5px" : "6px", alignItems: "baseline", minWidth: 0 }}>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: m ? "13.8px" : "15px", lineHeight: m ? "18.4px" : "20px", color: INK, whiteSpace: "nowrap" }}>
                        <Rupee value={raised} />
                      </Typography>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: m ? "11px" : "12px", lineHeight: m ? "14.7px" : "16px", color: "#7D7D7D", whiteSpace: "nowrap" }}>
                        raised of <Rupee value={goal} />
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: m ? "11px" : "12px", lineHeight: m ? "14.7px" : "16px", color: RED, whiteSpace: "nowrap" }}>
                      {pct}% funded
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Impact line */}
            <Box sx={{ width: "100%", boxSizing: "border-box", background: "#FFF1F1", border: `${m ? "0.92px" : "1px"} solid rgba(240,73,81,0.2)`, borderRadius: m ? "11px" : "12px", px: m ? "12px" : "16px", py: m ? "10px" : "12px", display: "flex", gap: m ? "8px" : "10px", alignItems: "center" }}>
              <Icon src={heartRed} size={m ? "18.4px" : "20px"} />
              <Typography sx={{ flex: 1, fontFamily: FONT, fontWeight: 500, fontSize: m ? "12px" : "14px", lineHeight: m ? "18.4px" : "20px", color: INK }}>
                Your{" "}
                <Box component="span" sx={{ fontWeight: m ? 500 : 700 }}>
                  <Rupee value={payAmount} />
                  {frequencyLabel}
                </Box>{" "}
                helps restore this temple
              </Typography>
            </Box>

            {chips.length > 0 && (
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: m ? "8px" : "10px", alignItems: "center" }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: m ? "11px" : "13px", lineHeight: m ? "16.5px" : "18px", color: "#7D7D7D" }}>Prefer a smaller amount?</Typography>
                <Box role="radiogroup" aria-label="Smaller amount" sx={{ display: "flex", gap: m ? "8px" : "10px", flexWrap: "wrap", justifyContent: "center" }}>
                  {chips.map((v) => {
                    const selected = chosenBase === v;
                    return (
                      <Box
                        key={v}
                        component="button"
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setChosenBase(selected ? null : v)}
                        sx={{
                          borderRadius: "100px",
                          border: `${m ? "1.1px" : "1.2px"} solid ${selected ? RED : "rgba(29,29,27,0.18)"}`,
                          background: selected ? "#FFF1F1" : "#FFFFFF",
                          px: m ? "16.5px" : "18px",
                          py: m ? "4.6px" : "8px",
                          cursor: "pointer",
                          fontFamily: FONT,
                          fontWeight: 700,
                          fontSize: m ? "12.9px" : "14px",
                          lineHeight: m ? "16.5px" : "18px",
                          color: selected ? RED : INK,
                          transition: "border-color 0.15s, background 0.15s, color 0.15s",
                          "&:focus-visible": { outline: `2px solid ${INK}`, outlineOffset: "2px" },
                        }}
                      >
                        <Rupee value={v} />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box sx={{ width: "100%", pt: m ? "3.7px" : "4px", display: "flex", flexDirection: "column", gap: "10px" }}>
              <Box
                component="button"
                type="button"
                onClick={complete}
                disabled={completing}
                sx={{ ...primaryBtnSx, ...(m ? { height: "38.6px", py: 0, px: "18px", gap: "9px", borderRadius: "11px", fontSize: "14px", lineHeight: "18.4px", boxShadow: "0px 9.2px 22px 0px rgba(240,73,81,0.35)" } : { py: "16px" }) }}
              >
                {completing ? <CircularProgress size={m ? 16 : 18} sx={{ color: "#FFFFFF" }} /> : <Icon src={heartWhite} size={m ? "16.5px" : "18px"} />}
                <span>
                  Complete My Seva · <Rupee value={payAmount} />
                </span>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setStep("remind")}
                sx={{ ...outlineBtnSx, ...(m && { height: "36.8px", py: 0, px: "18px", gap: "7.4px", borderRadius: "11px", border: "1.38px solid rgba(29,29,27,0.22)", fontSize: "12.9px", lineHeight: "18.4px" }) }}
              >
                <Icon src={bellDark} size={m ? "14.7px" : "16px"} inset="20.83% 18.75% 8.33% 18.75%" bleed="-5.29% -6%" />
                Remind Me Later
              </Box>
              <Box component="button" type="button" onClick={onLeave} sx={{ ...textLinkSx, color: "#9A9A9A", ...(m && { fontSize: "12px", lineHeight: "16.5px", py: "3.7px" }) }}>
                No thanks, leave anyway
              </Box>
            </Box>
          </Box>

          {!m && (
            <Box sx={{ width: "100%", background: "#F7F7F7", px: "20px", py: "14px", display: "flex", gap: "8px", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
              <Icon src={lockIcon} size="14px" inset="16.67% 20.83%" bleed="-6.25% -7.14%" />
              <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "12px", lineHeight: "16px", color: "#7D7D7D" }}>Secured by Razorpay</Typography>
            </Box>
          )}
        </Box>
      )}

      {step === "remind" && (
        <Box sx={{ px: m ? "16px" : "28px", pt: m ? "16px" : "30px", pb: m ? "12px" : "30px", display: "flex", flexDirection: "column", gap: m ? "8px" : "18px" }}>
          <CloseButton onClick={onClose} sx={closeSx} />
          <Box sx={{ display: "flex", gap: m ? "10px" : "14px", alignItems: "center", pr: m ? 0 : "40px" }}>
            <Box sx={{ width: m ? "36px" : "48px", height: m ? "36px" : "48px", borderRadius: m ? "18px" : "24px", background: "#FDEAEB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon src={bellRed} size={m ? "16.5px" : "22px"} inset="20.83% 18.75% 8.33% 18.75%" bleed="-5.29% -6%" />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, flex: 1 }}>
              <Typography id="ngo-reminder-title" component="h2" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: m ? "17px" : "20px", lineHeight: "26px", color: INK, pr: m ? "40px" : 0 }}>
                We'll Remind You
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: m ? "11.5px" : "13px", lineHeight: "19px", color: "#7D7D7D" }}>
                Share your details and the date you'd like to give.
              </Typography>
            </Box>
          </Box>

          <SavedDonation image={image} title={title} amount={payAmount} size={m ? "remind" : undefined} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: m ? "8px" : "14px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: m ? "4px" : "8px" }}>
              <Typography component="label" htmlFor="ngo-rem-name" sx={fieldLabelSx(m)}>Your Name *</Typography>
              <Box component="input" id="ngo-rem-name" type="text" autoComplete="name" placeholder="Enter your full name" value={name} onChange={(e) => setName(e.target.value)} sx={inputSx(errors.name, m)} />
            </Box>
            {/* Figma keeps mobile + email side by side on phones too */}
            <Box sx={{ display: "flex", gap: m ? "8px" : "12px", flexDirection: "row" }}>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: m ? "4px" : "8px" }}>
                <Typography component="label" htmlFor="ngo-rem-mobile" sx={fieldLabelSx(m)}>Mobile Number</Typography>
                <Box sx={{ position: "relative" }}>
                  <Typography sx={{ position: "absolute", left: m ? "12px" : "14px", top: m ? "8px" : "13px", fontFamily: FONT, fontSize: m ? "12px" : "14px", lineHeight: "20px", color: mobile ? INK : "#9A9A9A", pointerEvents: "none" }}>
                    +91
                  </Typography>
                  <Box
                    component="input"
                    id="ngo-rem-mobile"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="Mobile number"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    sx={{ ...inputSx(errors.mobile || errors.contact, m), pl: m ? "38px" : "48px" }}
                  />
                </Box>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: m ? "4px" : "8px" }}>
                <Typography component="label" htmlFor="ngo-rem-email" sx={fieldLabelSx(m)}>Email</Typography>
                <Box component="input" id="ngo-rem-email" type="email" autoComplete="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} sx={inputSx(errors.email || errors.contact, m)} />
              </Box>
            </Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: m ? "11px" : "12px", lineHeight: "16px", color: errors.contact ? RED : "#9A9A9A" }}>
              Enter at least one — mobile number or email.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: m ? "4px" : "8px" }}>
              <Typography component="label" htmlFor="ngo-rem-date" sx={fieldLabelSx(m)}>When would you like to offer your seva? *</Typography>
              <Box
                ref={dateAnchorRef}
                id="ngo-rem-date"
                component="button"
                type="button"
                aria-haspopup="dialog"
                onClick={() => setDateOpen(true)}
                sx={{
                  ...inputSx(errors.date, m),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  textAlign: "left",
                  color: date ? INK : "#9A9A9A",
                }}
              >
                {date ? date.format("D MMMM YYYY") : "Select a date"}
                <Icon src={calendarIcon} size="18px" inset="12.5% 14.58% 16.67% 14.58%" bleed="-5.29%" />
              </Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  open={dateOpen}
                  onClose={() => setDateOpen(false)}
                  value={date}
                  onChange={(v) => {
                    setDate(v);
                    setDateOpen(false);
                  }}
                  disablePast
                  maxDate={dayjs().add(1, "year")}
                  slotProps={{
                    textField: { sx: { display: "none" } },
                    popper: { anchorEl: dateAnchorRef.current, placement: "bottom-start", sx: { zIndex: 1600 } },
                    dialog: { sx: { zIndex: 1600 } },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          {formError && (
            <Typography role="alert" sx={{ fontFamily: FONT, fontSize: m ? "12px" : "13px", lineHeight: "18px", color: RED, mt: m ? 0 : "-6px" }}>
              {formError}
            </Typography>
          )}

          <Box sx={{ pt: "4px", display: "flex", flexDirection: "column", gap: m ? "6px" : "10px" }}>
            <Box component="button" type="button" onClick={saveReminder} disabled={saving} sx={{ ...primaryBtnSx, ...(m ? { height: "40px", py: 0, fontSize: "14px" } : { py: "15px" }) }}>
              {saving ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : <Icon src={bellWhite} size="18px" inset="20.83% 18.75% 8.33% 18.75%" bleed="-5.88% -6.67%" />}
              Set Reminder
            </Box>
            <Box component="button" type="button" onClick={() => setStep("recover")} sx={{ ...textLinkSx, color: "#7D7D7D", ...(m && { py: "1px" }) }}>
              ← Back to donation
            </Box>
          </Box>
        </Box>
      )}

      {step === "done" && (
        <Box sx={{ px: m ? "16px" : "30px", pt: m ? "28px" : "40px", pb: m ? "18px" : "30px", display: "flex", flexDirection: "column", gap: m ? "12px" : "16px", alignItems: "center" }}>
          <CloseButton onClick={onClose} sx={closeSx} />
          <Box sx={{ width: m ? "62px" : "76px", height: m ? "62px" : "76px", borderRadius: "50%", background: "#E9F8EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ width: m ? "44px" : "54px", height: m ? "44px" : "54px", borderRadius: "50%", background: "#22C55E", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon src={checkWhite} size={m ? "22.8px" : "28px"} inset="31.25% 20.83% 29.17% 20.83%" bleed="-13.68% -9.29%" />
            </Box>
          </Box>
          <Typography id="ngo-reminder-title" component="h2" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: m ? "20px" : "24px", lineHeight: "32px", color: INK, textAlign: "center" }}>
            Reminder Set!
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: m ? "13px" : "15px", lineHeight: "24px", color: "#6B6560", textAlign: "center" }}>
            We'll remind you on{" "}
            <Box component="span" sx={{ fontWeight: 700, color: INK }}>
              {date ? date.format("D MMMM YYYY") : ""}
            </Box>{" "}
            so you can complete your seva.
          </Typography>
          <SavedDonation image={image} title={title} amount={payAmount} size={m ? "done" : undefined} />
          <Box sx={{ width: "100%", pt: "6px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <Box component="button" type="button" onClick={complete} disabled={completing} sx={{ ...primaryBtnSx, ...(m ? { height: "44px", py: 0, fontSize: "14px" } : { py: "15px" }) }}>
              {completing ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : <Icon src={heartWhite} size="18px" />}
              <span>
                Offer Seva Now Instead · <Rupee value={payAmount} />
              </span>
            </Box>
            <Box component="button" type="button" onClick={onClose} sx={{ ...outlineBtnSx, ...(m && { height: "42px", py: 0 }) }}>
              Done
            </Box>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default CheckoutReminderModal;
